import { UserRound, Sparkles, Eye, Gem, Check } from "lucide-react";
import { PageShell, PageHero } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";
import { assetUrl } from "@/lib/api";

const TONES = ["bg-[#FDFBF7]", "bg-[#F6F1E8]", "bg-[#EFF3ED]", "bg-[#F8F0E9]"];

const VALUES = [
  { icon: UserRound, title: "Proximité", text: "Un projet immobilier reste profondément humain. L'échange et la compréhension de vos besoins passent avant les outils." },
  { icon: Sparkles, title: "Simplicité", text: "Vous aider à comprendre ce qui se passe, ce qui vient ensuite et ce qui nécessite une décision." },
  { icon: Eye, title: "Transparence", text: "Rendre les informations du projet plus accessibles et les étapes plus lisibles." },
  { icon: Gem, title: "Qualité du projet", text: "Ne pas seulement penser aux travaux, mais au résultat : un espace cohérent avec vos besoins et votre manière de vivre." },
];

const SIMPLER_POINTS = [
  "Des informations regroupées.",
  "Des étapes compréhensibles.",
  "Des interlocuteurs identifiés.",
  "Des décisions prises au bon moment.",
  "Un suivi adapté au niveau d'accompagnement choisi.",
];

export default function AboutPage() {
  return (
    <PageShell>
      <PageHero
        testid="page-apropos-hero"
        label="À propos de CozyHome"
        title={<>Les travaux devraient commencer par une envie, <span className="italic text-sage">pas par une inquiétude.</span></>}
        sub="Créer une maison, transformer un intérieur ou rénover un lieu de vie est un projet important. Mais entre les idées, le budget, les plans, les démarches, les devis, les professionnels et le chantier, il peut rapidement devenir difficile de savoir par où commencer — et comment garder le cap."
      />

      <section className="bg-ivory pb-20 sm:pb-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="max-w-3xl font-display text-2xl font-medium leading-snug text-navy sm:text-3xl">
              CozyHome est né d'une idée simple :{" "}
              <span className="italic text-cognac">rendre ces projets plus faciles à comprendre, à préparer et à suivre.</span>
            </p>
          </Reveal>

          <Reveal>
            <div className="mt-20 grid grid-cols-1 items-center gap-8 lg:grid-cols-2 lg:gap-16">
              <div className="overflow-hidden rounded-3xl">
                <img
                  src={assetUrl("/api/static/redesign/hero.webp")}
                  alt="Intérieur lumineux rénové, bois naturel et touches de vert"
                  loading="lazy"
                  className="aspect-[4/3] w-full object-cover"
                />
              </div>
              <div>
                <h2 className="font-display text-3xl font-medium tracking-tight text-navy sm:text-4xl">
                  Un seul projet. Beaucoup d'étapes. Un accompagnement qui les relie.
                </h2>
                <p className="mt-5 max-w-lg text-sm leading-relaxed text-stone-500 sm:text-base">
                  CozyHome vous accompagne depuis les premières réflexions jusqu'à
                  la réalisation de votre projet. Notre rôle n'est pas de décider à
                  votre place. Il est de vous aider à structurer vos idées,
                  comprendre les prochaines étapes, vous entourer des professionnels
                  adaptés et conserver une vision claire de votre projet.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal>
            <div className="mt-20 grid grid-cols-1 items-center gap-8 lg:grid-cols-2 lg:gap-16 lg:[&>*:first-child]:order-2">
              <div className="overflow-hidden rounded-3xl">
                <img
                  src={assetUrl("/api/static/redesign/conseil-pros.webp")}
                  alt="Artisan menuisier mesurant une pièce de chêne dans un atelier lumineux"
                  loading="lazy"
                  className="aspect-[4/3] w-full object-cover"
                />
              </div>
              <div>
                <h2 className="font-display text-3xl font-medium tracking-tight text-navy sm:text-4xl">
                  Votre maison doit d'abord vous ressembler.
                </h2>
                <p className="mt-5 max-w-lg text-sm leading-relaxed text-stone-500 sm:text-base">
                  Il n'existe pas une seule bonne façon d'habiter. Une famille qui
                  s'agrandit, une maison que l'on souhaite rénover, une nouvelle
                  construction, davantage de lumière, une pièce supplémentaire ou
                  simplement l'envie de se sentir mieux chez soi… Chaque projet
                  commence par une histoire différente.
                </p>
                <p className="mt-4 max-w-lg text-sm leading-relaxed text-stone-500 sm:text-base">
                  C'est pourquoi CozyHome part d'abord de vos besoins, de votre
                  façon de vivre et de vos priorités.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal>
            <div className="mt-20 rounded-3xl bg-navy px-8 py-14 text-center sm:px-14 sm:py-16">
              <p className="mx-auto max-w-3xl font-display text-3xl font-medium leading-snug text-white sm:text-4xl">
                « Un projet réussi n'est pas seulement beau.{" "}
                <span className="italic text-wood">Il doit être juste pour ceux qui vont y vivre.</span> »
              </p>
            </div>
          </Reveal>

          <Reveal>
            <div className="mt-20 grid grid-cols-1 items-start gap-8 lg:grid-cols-2 lg:gap-16">
              <h2 className="font-display text-3xl font-medium tracking-tight text-navy sm:text-4xl">
                Plus simple ne veut pas dire moins accompagné.
              </h2>
              <div>
                <p className="max-w-lg text-sm leading-relaxed text-stone-500 sm:text-base">
                  Nous voulons enlever de la complexité là où elle n'apporte rien :
                </p>
                <ul className="mt-5 space-y-3">
                  {SIMPLER_POINTS.map((p) => (
                    <li key={p} className="flex items-center gap-3 text-sm font-semibold text-navy/80 sm:text-base">
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-sage/15 text-sage">
                        <Check className="h-3 w-3" />
                      </span>
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Reveal>

          <div className="mt-20 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <article
                  data-testid={`apropos-value-${i}`}
                  className={`h-full rounded-3xl p-8 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-sm ${TONES[i % TONES.length]}`}
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-sage/15 text-sage">
                    <v.icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-6 text-base font-bold text-navy sm:text-lg">{v.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-stone-500">{v.text}</p>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div data-testid="apropos-local" className="mt-20 rounded-3xl bg-cream p-8 text-center sm:p-14">
              <p className="font-display text-2xl font-medium leading-snug text-navy sm:text-3xl">
                CozyHome, <span className="italic text-cognac">près de votre projet.</span>
              </p>
              <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-stone-500 sm:text-base">
                CozyHome se développe progressivement par secteurs afin de conserver
                un accompagnement de proximité, tout en s'appuyant sur une méthode
                et des outils communs.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </PageShell>
  );
}
